import { createContext, useContext, type ReactNode } from "react";

/** Realm roles of agent-platform (platform/services/identity/realm). */
export type Role =
  | "builder"
  | "approver"
  | "reviewer"
  | "auditor"
  | "platform-auditor"
  | "release-approver"
  | "operator"
  | "executive"
  | "model-admin"
  | "agent-admin"
  | "tenant-admin"
  | "pii-reader"
  | "privacy-officer"
  | "memory-curator"
  | "kill-switch-operator"
  | "finance"
  | "finance-approver";

export interface SessionUser {
  sub: string;
  username: string;
  name: string;
  email?: string;
  tenant: string;
  locale?: string;
  roles: string[];
  /** Work attributes from the token (`ap_unit`, `ap_station`, … and `ap_attributes`), for Command Centre placeholders such as `{user.unit}` (ADR-0117). */
  attributes?: Record<string, string>;
}

export interface AuthContextValue {
  user: SessionUser | null;
  /** Current access token from memory (never persisted). */
  getToken: () => string | null;
  logout: () => Promise<void>;
  /** Renews the access token silently (OIDC `signinSilent`); false when that is not possible. */
  refreshToken?: () => Promise<boolean>;
  /** Sign out and sign in again as another account (dev stacks with demo users only). */
  switchUser?: (username: string) => Promise<void>;
  mode: "oidc" | "test";
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  getToken: () => null,
  logout: async () => {},
  mode: "oidc",
});

export function AuthProvider({ value, children }: { value: AuthContextValue; children: ReactNode }) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

export interface RoleRequirement {
  /** At least one of these roles. */
  anyOf?: readonly string[];
  /** Every one of these roles. */
  allOf?: readonly string[];
}

/** Pure RBAC check on token roles. An empty requirement means "any signed-in user". */
export function hasRole(roles: readonly string[] | undefined, req: RoleRequirement): boolean {
  if (!roles) return false;
  const set = new Set(roles);
  if (req.allOf && !req.allOf.every((r) => set.has(r))) return false;
  if (req.anyOf && req.anyOf.length > 0 && !req.anyOf.some((r) => set.has(r))) return false;
  return true;
}

export function useHasRole(req: RoleRequirement): boolean {
  const { user } = useAuth();
  return !!user && hasRole(user.roles, req);
}

/**
 * Renders children only when the signed-in user's token roles satisfy the requirement.
 * UX only: every service enforces authorisation server-side (OPA).
 */
export function Can({ anyOf, allOf, fallback = null, children }: RoleRequirement & { fallback?: ReactNode; children: ReactNode }) {
  const ok = useHasRole({ anyOf, allOf });
  return <>{ok ? children : fallback}</>;
}
